<template>
  <div class="promotion-raffles-page">
    <a-row type="flex" justify="space-between">
      <a-col :lg="8" :md="8" :sm="10" :xs="24">
        <h2 class="list-title">
          <tickets-icon class="list-icon" />
          <span>{{ labels.title }}</span>
        </h2>
        <p class="list-subtitle">{{ labels.subtitle }}</p>
      </a-col>
      <a-col :lg="16" :md="16" :xs="24">
        <a-row
          type="flex"
          justify="end"
          :gutter="[0, 8]"
          :style="{
            alignItems: 'flex-end',
            marginTop: '25px',
            marginBottom: '10px',
            textAlign: 'right',
          }"
          class="row-reverse"
        >
          <a-col :xs="24" :md="21" :lg="21">
            <a-input-search
              :placeholder="labels.placeholderSearch"
              style="width: 100%"
              @search="handleSearch"
              allowClear
            />
          </a-col>
          <a-col :xs="24" :md="3" :lg="3" :style="{ textAlign: 'right' }">
            <router-link
              v-if="canCreate"
              :to="{ name: 'admin.promotion-raffles.create' }"
            >
              <a-button type="default" ghost icon="plus" class="add-button">
                <span>{{ labels.add }}</span>
              </a-button>
            </router-link>
          </a-col>
        </a-row>
      </a-col>
    </a-row>
    <a-row>
      <a-col :span="24">
        <StandardTable
          ref="standardTable"
          :fetchService="fetchPromotionRaffles"
          :columns="columns"
          :locale="locale"
          :handleShowTotal="handleShowTotal"
          v-bind="!isMobile && { scroll: { x: true } }"
          :paginationType="isMobile ? 'arrows' : 'numbers'"
          :customRow="handleCustomRow"
          :showSizeChanger="true"
          :pageSizeOptions="['10', '20', '50', '100']"
        />
      </a-col>
    </a-row>
  </div>
</template>

<script>
import _ from "lodash";
import labels from "@/utils/labels";
import StandardTable from "../../../components/core/table/StandardTable.vue";
import TicketsIcon from "../../../assets/icons/TicketsIcon.vue";
import { mapActions, mapGetters, mapState } from "vuex";
import {
  DATE_FORMAT,
  adminPageTitle,
  hasRoles,
  roles,
} from "../../../utils/utils";
const initialState = {};
export default {
  components: { StandardTable, TicketsIcon },
  name: "PromotionRaffleList",
  metaInfo: {
    title: adminPageTitle("Rifas de promoción"),
  },
  filters: {
    ucwords(value) {
      if (!value) return "";
      value = value.toString();
      return value
        .split(" ")
        .map((item) => _.capitalize(item))
        .join(" ");
    },
  },
  data() {
    return {
      labels: labels.promotionRaffles,
      search: "",
      showFilter: false,
      filters: _.cloneDeep(initialState),
      activeRow: null,
    };
  },
  methods: {
    ...mapActions("promotionRaffles", ["fetchPromotionRaffles"]),
    statusColor(status = "success") {
      switch (status) {
        case "confirmed":
          return "#28a745";
        case "processing":
        case "pending":
          return "#ffc107";
        case "closed":
          return "#f50";
        case "initiated":
          return "#108ee9";
        default:
          return "";
      }
    },
    statusText(status = this.labels.table.columns.status.pending) {
      return this.labels.table.columns?.status[status] ?? " - ";
    },
    handleReloadTable() {
      let filtersTable = this.$refs.standardTable.filters;
      let result = { ...filtersTable };
      this.$refs.standardTable.handleFilters(result);
    },
    handleSearch(search) {
      this.$refs.standardTable.handleSearch(search);
    },
    handleShowTotal(total, range) {
      return `${range[0]}-${range[1]} de ${total}`;
    },
    handleCustomRow(_, index) {
      return {
        on: {
          click: () => {
            this.activeRow = this.activeRow === index ? null : index;
          },
        },
      };
    },
  },
  computed: {
    ...mapGetters("adminAuth", ["getLoggedUser"]),
    ...mapState("layout", ["screenWidth"]),
    isMobile() {
      return this.screenWidth < 600;
    },
    locale() {
      return {
        emptyText: this.labels.table.emptyText,
      };
    },
    canCreate() {
      const { accessRoles } = this.getLoggedUser;
      return hasRoles(accessRoles, [roles.admin.ROLE_PROMOTION_RAFFLES_CREATE]);
    },
    columns() {
      return [
        {
          title: this.labels.table.columns.name,
          dataIndex: "name",
          key: "name",
          sorter: true,
          hidden: this.isMobile,
          customRender: (text) => {
            return `${text}`;
          },
        },
        {
          title: this.labels.table.columns.start_sequence,
          dataIndex: "start_sequence",
          key: "start_sequence",
          sorter: true,
          hidden: this.isMobile,
          customRender: (text) => {
            return `${text}`;
          },
        },
        {
          title: this.labels.table.columns.end_sequence,
          dataIndex: "end_sequence",
          key: "end_sequence",
          sorter: true,
          hidden: this.isMobile,
          customRender: (text) => {
            return `${text}`;
          },
        },
        {
          title: this.labels.table.columns.start_date,
          dataIndex: "start_date",
          key: "start_date",
          sorter: true,
          hidden: this.isMobile,
          customRender: (text) => {
            return this.$moment(text).format("DD/MM/YYYY HH:mm:ss");
          },
        },
        {
          title: this.labels.table.columns.status.title,
          dataIndex: "status",
          key: "status",
          sorter: true,
          hidden: this.isMobile,
          customRender: (text) => {
            return (
              <a-tag color={this.statusColor(text?.toLowerCase())}>
                {this.statusText(text?.toLowerCase())}
              </a-tag>
            );
          },
        },
        {
          title: this.labels.table.columns.winning_ticket,
          dataIndex: "winning_ticket",
          key: "winning_ticket",
          hidden: this.isMobile,
          customRender: (text) => {
            return text ? text : "N/A";
          },
        },
        {
          title: this.labels.table.columns.action.title,
          dataIndex: "action",
          key: "action",
          width: "120px",
          hidden: this.isMobile,
          customRender: (_, record) => {
            return (
              <div class="action-buttons">
                <router-link
                  to={{
                    name: "admin.promotion-raffles.edit",
                    params: { id: record.id },
                  }}
                  title={this.labels.table.columns.action.edit}
                >
                  <a-button
                    type="link"
                    icon="edit"
                    class="action-button-edit"
                  />
                </router-link>
              </div>
            );
          },
        },
        {
          title: this.labels.table.columns.promotionRaffles,
          dataIndex: "promotionRaffles",
          key: "promotionRaffles",
          sorter: false,
          hidden: !this.isMobile,
          customRender: (_text, record, index) => {
            // Card item design
            const color = this.statusColor(record.status?.toLowerCase());
            return (
              <div class="list-item">
                <div class="list-item-head">
                  <div
                    class="list-item-head-header"
                    style={{ borderColor: color }}
                  >
                    <span>
                      {this.$options.filters.ucwords(
                        this.$moment(record.start_date).format(
                          DATE_FORMAT.MOMENT_DAY_MONTH_YEAR
                        )
                      )}
                    </span>
                    <small>
                      {this.$moment(record.start_date)
                        .format(DATE_FORMAT.MOMENT_TIME)
                        .toUpperCase()}
                    </small>
                  </div>
                  <div class="list-item-head-content">
                    <div class="list-item-head-content-name">
                      <span>{record.name}</span>
                    </div>
                    <div class="list-item-head-content-start_sequence">
                      <span>{record.start_sequence}</span>
                    </div>
                    <div class="list-item-head-content-end_sequence">
                      <span>{record.end_sequence}</span>
                    </div>
                  </div>
                  <div class="list-item-head-footer">
                    {record.winning_ticket && (
                      <div class="list-item-head-footer-winner">
                        <span>{record.winning_ticket}</span>
                      </div>
                    )}
                    <div class="list-item-head-footer-status">
                      <a-tag color={color}>
                        {this.statusText(record?.status?.toLowerCase())}
                      </a-tag>
                    </div>
                  </div>
                </div>
                {this.activeRow === index && (
                  <div class="list-item-actions">
                    <a-button-group>
                      <router-link
                        class="edit-button ant-btn ant-btn-primary ant-btn-promotionRaffle"
                        to={{
                          name: "admin.promotion-raffles.edit",
                          params: { id: record.id },
                        }}
                        title={this.labels.table.columns.action.edit}
                      >
                        {this.labels.table.columns.action.edit}
                      </router-link>
                    </a-button-group>
                  </div>
                )}
              </div>
            );
          },
        },
      ].filter((column) => !column.hidden);
    },
    filtered() {
      return !_.isEqual(this.filters, initialState);
    },
    serverDateFormat() {
      return "YYYY-MM-DD HH:mm:ss";
    },
  },
};
</script>
<style lang="scss">
@import "../../../assets/scss/variable";
.promotion-raffles-page {
  .list {
    &-title {
      margin-bottom: 10px;
      .list-icon {
        margin-right: 10px;
      }
    }
    &-subtitle {
      margin-bottom: 10px;
    }
    &-icon {
      svg {
        fill: black;
        height: 1.3em;
        width: 1.3em;
        stroke: black;
      }
    }
  }
  .add-button {
    color: color(primary);
    border-color: color(primary);
    margin-left: 10px;
  }
  .action-buttons {
    display: flex;
    justify-content: space-between;
    .action-button-edit {
      color: color(primary);
    }
    .action-button-delete {
      color: color(danger);
    }
  }
  @media screen and (max-width: $screen-mobile) {
    .row-reverse {
      flex-direction: column-reverse;
    }
  }
  .list-item {
    display: flex;
    flex-direction: column;
    gap: 4px;
    &-head {
      display: flex;
      flex-direction: row;
      justify-content: space-between;
      gap: 6px;
      &-header {
        display: flex;
        flex-direction: column;
        justify-content: center;
        align-items: center;
        gap: 2px;
        max-width: 25%;
        width: 25%;
        border-right: 4px solid color(border);
        .success {
          color: color(success);
        }
        .warning {
          color: color(warning);
        }
        .danger {
          color: color(danger);
        }
        span {
          font-size: 14px;
          font-weight: 600;
        }
        small {
          font-size: 12px;
          font-weight: 400;
          padding-right: 4px;
        }
      }
      &-content {
        display: flex;
        flex-direction: column;
        justify-content: center;
        align-items: flex-start;
        gap: 2px;
        max-width: 50%;
        width: 50%;
        overflow: hidden;
        &-name,
        &-start_sequence,
        &-end_sequence {
          span {
            font-size: 16px;
            font-weight: 600;
            text-overflow: ellipsis;
          }
        }
      }
      &-footer {
        max-width: 25%;
        width: 25%;
        display: flex;
        flex-direction: column;
        justify-content: center;
        align-items: center;
        text-align: right;
        gap: 2px;
        &-winner {
          span {
            font-size: 14px;
            font-weight: 600;
          }
        }
        &-status {
          width: 100%;
          span {
            font-size: 12px;
            font-weight: 400;
          }
          .ant-tag {
            padding: 0;
            margin: 0;
            padding-left: 2px;
            padding-right: 2px;
          }
        }
      }
    }
    &-actions {
      width: 100%;
      margin-top: 10px;
      .ant-btn-group {
        width: 100%;
        .more-info-button,
        .edit-button,
        .delete-button {
          width: 100%;
        }
      }
    }
  }
}
.w-100 {
  display: block;
  width: 100%;
  margin-bottom: 5px;
}
</style>
